const __dblEpsilon = 0.00000001; //Number.EPSILON;

export function isUndef(val) {
    return null === val || undefined === val;
}

export function doubleEqual(d1, d2) {
    return Math.abs(d1 - d2) < __dblEpsilon;
}

export function deepEqual(
    a,
    b,
    opts = {}
): boolean {
    if (a === b) return true;

    const normUndef = (v: any) => {
    if (opts.nullEqualsUndef && v === null) return undefined;
    if (opts.nullyNullEqualsUndef && isNull(v)) return undefined;
        return v;
    };

    if (normUndef(a) === normUndef(b)) {
        return true;
    }

    if (isUndef(a) || isUndef(b)) return false;

    if ("number" === typeof a && "number" === typeof b) {
        return doubleEqual(a, b);
    }

    const nextOpts = {
        ...opts,
        skippedKeys: opts.skippedKeysAreRecursive
            ? opts.skippedKeys
            : undefined,
        onlyKeys: undefined,
    };

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length == b.length) {
            let equal = true;

            for (let i = 0; i < a.length; i++) {
                equal = deepEqual(a[i], b[i], nextOpts);
                if (!equal) {
                    break;
                }
            }

            return equal;
        }

        return false;
    }

    if ("object" === typeof a && "object" === typeof b) {
        return __compareWithKeys(
            a,
            b,
            (ka, kb, va, vb) =>
                deepEqual(ka, kb, nextOpts) && deepEqual(va, vb, nextOpts),
            opts
        );
    }

    return false;
}

// helpers for deepEqual()

function _gkeys(v: any): any[] {
    return typeof v.keys === "function" ? Array.from(v.keys()) : Object.keys(v);
}

function _gget(v: any, k: string) {
    return typeof v.get === "function" ? v.get(k) : v[k];
}

function _normUndef(v, opts) {
    if (opts.nullEqualsUndef && v === null) return undefined;
    if (opts.nullyNullEqualsUndef && isNull(v)) return undefined;
    return v;
}

function __compareWithKeys(
    a: object,
    b: object,
    cmp: (keyA: any, keyB: any, valA: any, valB: any) => boolean,
    opts: {
    onlyKeys?: any[];
    skippedKeys?: any[];
    intersectKeys?: boolean;
    ignoreUndefKeys?: boolean;
    nullEqualsUndef?: boolean;
    nullyNullEqualsUndef?: boolean;
    } = {}
): boolean {
    let ka = _gkeys(a),
    kb = _gkeys(b);

    if (opts.onlyKeys) {
        ka = kb = opts.onlyKeys;
    } else if (opts.intersectKeys) {
        ka = kb = arrayIntersect(ka, kb);
    }

    if (opts.ignoreUndefKeys) {
        ka = ka.filter(
            (k) => !(opts.skippedKeys || []).includes(k) && _gget(a, k) !== undefined
        );
        kb = kb.filter(
            (k) => !(opts.skippedKeys || []).includes(k) && _gget(b, k) !== undefined
        );
    }

    let restKeys: string[] = undefined;

    if (opts.nullEqualsUndef || opts.nullyNullEqualsUndef) {
        restKeys = [];

        restKeys.push(...ka.filter((k) => !kb.includes(k)));
        restKeys.push(...kb.filter((k) => !ka.includes(k)));

        ka = kb = arrayIntersect(ka, kb);
    }

    if (ka.length === kb.length) {
        let equal = true;

        ka.sort(), kb.sort();

        for (let i = 0; i < ka.length; i++) {
            if (opts.skippedKeys && opts.skippedKeys.includes(ka[i])) {
                continue;
            }
            equal = cmp(ka[i], kb[i], _gget(a, ka[i]), _gget(b, kb[i]));
            if (!equal) {
                break;
            }
        }

        if (equal && restKeys && restKeys.length > 0) {
            for (const k of restKeys) {
                const v1 = _gget(a, k),
                    v2 = _gget(b, k);

                equal = v1 === v2 || _normUndef(v1, opts) === _normUndef(v2, opts);

                if (!equal) {
                    break;
                }
            }
        }

        return equal;
    }

    return false;
}

export function throttled(
    limitMillis,
    fn: (...args) => void
) {
    if (limitMillis <= 0) {
      return fn;
    }

    let timeout;
    let lastRun;

    return function (...iargs) {
        const now = Date.now();

        if (!lastRun) {
            fn(...iargs);
            lastRun = now;
        } else {
            clearTimeout(timeout);

            timeout = setTimeout(() => {
            const now = Date.now();

            if (now - lastRun >= limitMillis) {
                fn(...iargs);
                lastRun = now;
            }
            }, Math.max(0, limitMillis - (now - lastRun)));
        }
    };
  }


// WEBPACK FOOTER //
// ./lib/kl-utils.js